import React, { useState } from 'react';
import { Button, FormErrors } from '@squirrellearningltd/go-teacher-components';
import cx from 'classnames';
import { useMutation } from '@apollo/react-hooks';
import styles from './UploadClassesPupils.module.scss';
import { MANAGE_IMPORT_CLASSES_STUDENTS } from '../../services/graphql';

const ImportSummaryErrors = ({ setManageDone, close, ticketId, isOnboarding, notValidStudents, onUpdateSetupStep, updatingStep }) => {


    const notValidExceptDuplicates = notValidStudents.filter(s => s.missingFirstName || s.missingLastName || s.missingStudentsClass || s.missingYearGroup || s.duplicatedUpn);
    const duplicates = notValidStudents.filter(s => s.duplicated || s.duplicatedInFile)

    const [includeDuplicates, setIncludeDuplicates] = useState([]);
    const [manageErrors, setManageErrors] = useState([]);


    const onSelectStudent = e => {
        if (includeDuplicates.indexOf(e.target.value) !== -1) {
            setIncludeDuplicates(includeDuplicates.filter(t => t !== e.target.value));
        } else {
            setIncludeDuplicates([...includeDuplicates, e.target.value]);
        }
    }

    // send ids for students to be ignored from import;
    const [manageImport, { loading }] = useMutation(MANAGE_IMPORT_CLASSES_STUDENTS, {
        update: (_, { data }) => {
            if (data.manageImportStudentsFromXlsx.errors && data.manageImportStudentsFromXlsx.errors.length !== 0) {
                setManageErrors(data.manageImportStudentsFromXlsx.errors);
                return;
            }
            if (data.manageImportStudentsFromXlsx.importTicket.errors && data.manageImportStudentsFromXlsx.errors.length !== 0) {
                setManageErrors(data.manageImportStudentsFromXlsx.errors);
                return;
            }
            if (data.manageImportStudentsFromXlsx.importTicket && data.manageImportStudentsFromXlsx.importTicket.id) {
                setManageDone();
            }
        },
    });


    return (
        <>
            <div className={styles.previewErrorsHeader}>
                <div>
                    <h3>Oops, we encountered a few problems…</h3>
                    {notValidExceptDuplicates.length > 0 ?
                        <p className="u-m-base-1">Sorry, we were unable to process your uploaded file. Please check your file for the following issues and try again:</p> :
                        <p className='u-m-base-2'>We've found the following students with the same names, please review to see if these are duplicates or unique students.</p>
                    }
                </div>
                <div className={styles.previewErrorsHeaderSide}>
                    <p className="heavy">Need help?</p>
                    <p>support@boomhub.app</p>
                </div>
            </div>
            <div className={styles.previewErrors}>
                {notValidExceptDuplicates.length > 0 ? (
                    <div>
                        <p className='lead'>Missing data</p>
                        <p className='u-m-base-2'>Please make sure that each student has a valid value for first name, last name, year group and class.</p>
                        <ul className={styles.previewErrorsList}>
                            {notValidExceptDuplicates.map(st => (
                                <li key={`st-${st.id}`}>
                                    <span className='u-m-right-2'>Line {st.line}:</span>
                                    <span className='u-m-right-2'> {st.missingFirstName ? 'Missing first name' : st.firstName} {st.missingLastName ? 'Missing last name' : st.lastName}</span>
                                    {st.missingYearGroup && <span className='u-m-right-1'>Missing year group</span>}
                                    {st.missingStudentsClass && <span className='u-m-right-1'>Missing class name</span>}
                                    {st.duplicatedUpn && <span>Duplicated UPN</span>}
                                </li>
                            ))}
                        </ul>


                        <p className='u-m-base-3'><a href="https://boomapps.s3.eu-west-2.amazonaws.com/sample-pupils-class-upload.xlsx" className='u-text-primary link-underline u-m-base-2' target="_blank" rel="noopener noreferrer">Download Template</a></p>

                        <Button onClick={close} className='u-m-right-2'>Start over</Button>
                        {isOnboarding &&
                            <button disabled={updatingStep} onClick={onUpdateSetupStep} className="btn-reset link-underline u-text-primary u-m-left-2">{updatingStep ? 'Saving...' : 'I\'ll do this later'}</button>
                        }
                    </div>
                ) : <>
                    <div>
                        <p>Check the students you wish to import</p>
                        <ul className={cx(styles.previewErrorsList, 'list-group')}>
                            {duplicates.map(st => (
                                <li className='list-item' key={`st-d-${st.id}`}>
                                    <div className="basic-form__group basic-form__group--check u-m-base-0 u-m-top-1">
                                        <input
                                            type='checkbox'
                                            className="basic-form__check-box"
                                            value={st.id}
                                            onChange={(e) => onSelectStudent(e)}
                                            id={`t-${st.id}`}
                                            checked={includeDuplicates.indexOf(st.id) > -1}
                                        />
                                        <label className="basic-form__check-label" htmlFor={`t-${st.id}`}>&nbsp;</label>
                                    </div>
                                    <span className='u-m-right-2'>Line {st.line}:</span>
                                    <span className='u-m-right-2'> {st.firstName} {st.lastName}</span>
                                    {st.duplicated && <span className='u-m-right-1'>Duplicated</span>}
                                    {st.duplicatedInFile && <span>Duplicated in File</span>}
                                </li>
                            ))}
                        </ul>
                        {manageErrors && <FormErrors errors={manageErrors} />}
                        <Button
                            className='u-m-right-2'
                            onClick={() =>
                                manageImport({
                                    variables: {
                                        importTicketId: ticketId,
                                        importedStudentIds: [...duplicates.filter(d => !includeDuplicates.includes(d.id)).map(d => d.id)]
                                    }
                                })
                            }
                            disabled={loading}
                        >Import</Button>
                    </div>
                </>
                }

                <div className={styles.panel}>
                    <p className="heavy u-m-base-2">Group codes</p>
                    <p className='u-m-base-2'>Our app needs specific values to be able to correctly identify year groups.</p>
                    <p className='u-m-base-2'>For Nursery and Reception please use ’N’ and ‘R’ respectively. For other year groups please enter just the numerical year number.</p>
                    <p className='u-m-base-2'>The following shows some examples:</p>
                    <div className={cx(styles.uploadStartYearGroup, styles.uploadStartYearGroupExtra)}>
                        <table>
                            <thead>
                                <tr>
                                    <th className='u-p-base-1'>Year Group Name</th>
                                    <th className='u-p-base-1'>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Nursery</td>
                                    <td>N</td>
                                </tr>
                                <tr>
                                    <td>Reception</td>
                                    <td>R</td>
                                </tr>
                                <tr>
                                    <td>Year 1</td>
                                    <td>1</td>
                                </tr>
                                <tr>
                                    <td>Year 2</td>
                                    <td>2</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </>
    )
}

export default ImportSummaryErrors;